<template>
  <div
    id="app"
    class="app"
    v-bind:class="{
      'app-sidebar-minified': appOptions.appSidebarMinified,
      'app-sidebar-mobile-toggled': appOptions.appSidebarMobileToggled,
      'app-sidebar-mobile-closed': appOptions.appSidebarMobileClosed,
      'app-content-full-height': appOptions.appContentFullHeight,
      'app-content-full-width': appOptions.appContentFullWidth,
      'app-without-sidebar': appOptions.appWithoutSidebar,
      'pt-0': appOptions.appWithoutHeader,
      'app-boxed-layout': appOptions.appBoxedLayout,
      'app-footer-fixed': appOptions.appWithFooter,
    }"
  >
    <template v-if="marketError && !marketRetrying">
      <main class="d-flex min-vh-100 align-items-center justify-content-center bg-light p-4">
        <b-card class="text-center" style="max-width: 32rem">
          <h1 class="h3">Market configuration unavailable</h1>
          <p class="text-muted">{{ marketError }}</p>
          <b-button variant="primary" :disabled="marketRetrying" @click="retryMarketBootstrap">
            {{ marketRetrying ? 'Retrying...' : 'Retry' }}
          </b-button>
        </b-card>
      </main>
    </template>
    <template v-else-if="marketLoading">
      <main class="d-flex min-vh-100 align-items-center justify-content-center bg-light p-4" role="status" aria-live="polite">
        <b-card class="text-center" style="max-width: 32rem">
          <h1 class="h3">Loading market context</h1>
          <p class="text-muted mb-0">Please wait while we load the selected market and its dashboard data.</p>
        </b-card>
      </main>
    </template>
    <template v-else>
      <Header v-if="!appOptions.appWithoutHeader" />
      <Sidebar v-if="!appOptions.appWithoutSidebar" />
      <router-view />
      <Footer v-if="appOptions.appWithFooter" />
    </template>
  </div>
</template>

<script>
import AppOptions from './config/app-options'
import Sidebar from './components/Sidebar.vue'
import Header from './components/Header'
import Footer from './components/Footer.vue'

export default {
  name: 'App',
  data() {
    return {
      appOptions: AppOptions,
      marketRetrying: false,
    }
  },
  components: { Sidebar, Header, Footer },
  computed: {
    marketContextRequired() {
      return this.$store.getters['auth/authenticated'] && this.$store.getters['market/enabled']
    },
    marketLoading() {
      return this.marketContextRequired && (
        !this.$store.getters['market/ready']
        || this.$store.getters['market/loading']
        || this.marketRetrying
      )
    },
    marketError() {
      return this.marketContextRequired && this.$store.getters['market/error']
    },
    marketFallbackApplied() {
      return this.$store.getters['market/fallbackApplied']
    },
    activeMarket() {
      return this.$store.getters['market/activeMarket']
    },
  },
  methods: {
    async retryMarketBootstrap() {
      if (this.marketRetrying) return

      this.marketRetrying = true
      try {
        const ready = await this.$store.dispatch('market/bootstrap', true)

        if (ready) {
          await this.$store.dispatch('base/getBase')

          if (!this.$store.getters['market/error']) {
            window.location.reload()
            // Keep routes unmounted until the new document takes over.
            return
          }
        }
      } catch (error) {
        await this.$store.dispatch('market/failContext',
          this.$store.getters['market/error'] || 'The selected market dashboard data could not be loaded. Please retry.')
      }

      this.marketRetrying = false
    },
  },
  mounted() {
    this.$insProgress.finish()

    if (this.marketFallbackApplied) {
      this.$bvToast.toast(
        `The saved market is unavailable. Switched to ${this.activeMarket?.name || 'the default market'}.`,
        {
          title: 'Market selection updated',
          variant: 'warning',
        },
      )
    }
  },
  created() {
    this.$insProgress.start()

    this.$router.beforeEach((to, from, next) => {
      this.$insProgress.start()

      if (this.appOptions.appSidebarMobileToggled == true) {
        this.appOptions.appSidebarMobileClosed = true

        setTimeout(() => {
          this.appOptions.appSidebarMobileClosed = false
          this.appOptions.appSidebarMobileToggled = false
        }, 250)
      }
      next()
    })

    this.$router.afterEach(() => {
      this.$insProgress.finish()
    })
  },
}
</script>
